import { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface LibraryHeroSurfaceProps {
  children: ReactNode
  /** Rendered as a `<section>` with this label when set, else a `<div>`. */
  ariaLabel?: string
  testId: string
  className?: string
}

/**
 * The Library's deep editorial surface, shared by the featured banner and
 * the collection banner so the two always read as one product. The fill is
 * the theme's deep brand color, with no texture. In dark mode a light primary
 * wash keeps the panel from sitting flush with the page.
 */
export function LibraryHeroSurface(props: LibraryHeroSurfaceProps) {
  const { children, ariaLabel, testId, className } = props

  const Tag = ariaLabel ? 'section' : 'div'

  return (
    <Tag
      aria-label={ariaLabel}
      className={cn(
        'relative overflow-hidden rounded-[24px] border border-primary-dark-foreground/10 bg-primary-dark text-primary-dark-foreground dark:border-primary/25',
        className,
      )}
      data-testid={testId}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden bg-primary/10 dark:block" />
      {children}
    </Tag>
  )
}

/** The pill over a hero surface ("Featured collection", a preset). */
export const LIBRARY_HERO_LABEL_CLASS =
  'inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[13px] font-medium text-primary-foreground shadow-sm'

/** The display headline on a hero surface (48 to 60 px). */
export const LIBRARY_HERO_TITLE_CLASS =
  'text-balance pb-[0.12em] font-display text-[36px] font-normal leading-[1.08] tracking-[-0.015em] text-primary-dark-foreground md:text-[48px] xl:text-[60px]'

/** The meta line on a hero surface: muted, with strong numbers. */
export const LIBRARY_HERO_META_CLASS = 'text-sm text-primary-dark-foreground/70'

/** The number inside a hero count ("5" in "5 resources"). */
export const LIBRARY_HERO_NUMBER_CLASS = 'font-medium tabular-nums text-primary-dark-foreground'

/** The outline action on a hero surface. */
export const LIBRARY_HERO_OUTLINE_ACTION_CLASS =
  'border-primary-dark-foreground/25 bg-transparent text-primary-dark-foreground hover:bg-primary-dark-foreground/10 hover:text-primary-dark-foreground'

/** The primary action on a hero surface. */
export const LIBRARY_HERO_PRIMARY_ACTION_CLASS =
  'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md'
